import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AppDateInput from "../inputs/AppDateInput";
import { PropertyResponse } from "../../models/ResponseModels/PropertyResponse";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";

type GroupBy = "unit" | "property" | "month";
type PaymentStatus = "unpaid" | "deposit" | "paid" | null;

interface StatisticsFilterDialogProps {
  open: boolean;
  onClose: () => void;
  from: string;
  until: string;
  setFrom: (value: string) => void;
  setUntil: (value: string) => void;
  days: number;
  setDateRangeFromToday: (days: number) => void;
  setThisMonth: () => void;
  selectedPropertyId: number | "";
  setSelectedPropertyId: (value: number | "") => void;
  selectedUnitId: number | "";
  setSelectedUnitId: (value: number | "") => void;
  groupBy: GroupBy;
  setGroupBy: (value: GroupBy) => void;
  paymentStatus: PaymentStatus;
  setPaymentStatus: (value: PaymentStatus) => void;
  properties: PropertyResponse[];
  filteredUnits: UnitResponse[];
  onApply: () => void;
  t: (key: string, values?: Record<string, string | number>) => string;
}

const StatisticsFilterDialog = ({
  open,
  onClose,
  from,
  until,
  setFrom,
  setUntil,
  days,
  setDateRangeFromToday,
  setThisMonth,
  selectedPropertyId,
  setSelectedPropertyId,
  selectedUnitId,
  setSelectedUnitId,
  groupBy,
  setGroupBy,
  paymentStatus,
  setPaymentStatus,
  properties,
  filteredUnits,
  onApply,
  t,
}: StatisticsFilterDialogProps) => {
  const handleApply = () => {
    onApply();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 950 }}>
        {t("statistics.filters")}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            <Button
              variant={days === 7 ? "contained" : "outlined"}
              onClick={() => setDateRangeFromToday(7)}
            >
              {t("statistics.sevenDays")}
            </Button>

            <Button
              variant={days === 14 ? "contained" : "outlined"}
              onClick={() => setDateRangeFromToday(14)}
            >
              {t("statistics.fourteenDays")}
            </Button>

            <Button
              variant={days === 30 ? "contained" : "outlined"}
              onClick={() => setDateRangeFromToday(30)}
            >
              {t("statistics.thirtyDays")}
            </Button>

            <Button variant="outlined" onClick={setThisMonth}>
              {t("statistics.thisMonth")}
            </Button>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <AppDateInput
              label={t("calendar.startDate")}
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />

            <AppDateInput
              label={t("calendar.endDate")}
              value={until}
              onChange={(event) => setUntil(event.target.value)}
            />
          </Stack>

          <FormControl fullWidth>
            <InputLabel>{t("properties.title")}</InputLabel>
            <Select
              label={t("properties.title")}
              value={selectedPropertyId}
              onChange={(event) =>
                setSelectedPropertyId(event.target.value as number | "")
              }
            >
              <MenuItem value="">{t("statistics.allProperties")}</MenuItem>
              {properties.map((property) => (
                <MenuItem key={property.id} value={property.id}>
                  {property.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t("booking.unit")}</InputLabel>
            <Select
              label={t("booking.unit")}
              value={selectedUnitId}
              onChange={(event) =>
                setSelectedUnitId(event.target.value as number | "")
              }
            >
              <MenuItem value="">{t("statistics.allUnits")}</MenuItem>
              {filteredUnits.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.property?.name ? `${unit.property.name} · ` : ""}
                  {unit.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 900 }}>
              {t("statistics.groupBy")}
            </Typography>

            <ButtonGroup fullWidth>
              <Button
                variant={groupBy === "unit" ? "contained" : "outlined"}
                onClick={() => setGroupBy("unit")}
              >
                {t("statistics.unit")}
              </Button>

              <Button
                variant={groupBy === "property" ? "contained" : "outlined"}
                onClick={() => setGroupBy("property")}
              >
                {t("statistics.property")}
              </Button>

              <Button
                variant={groupBy === "month" ? "contained" : "outlined"}
                onClick={() => setGroupBy("month")}
              >
                {t("statistics.month")}
              </Button>
            </ButtonGroup>
          </Stack>

          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 900 }}>
              {t("statistics.payment")}
            </Typography>

            <ButtonGroup fullWidth>
              <Button
                variant={paymentStatus === null ? "contained" : "outlined"}
                onClick={() => setPaymentStatus(null)}
              >
                {t("common.all")}
              </Button>

              <Button
                variant={paymentStatus === "unpaid" ? "contained" : "outlined"}
                onClick={() => setPaymentStatus("unpaid")}
              >
                {t("booking.unpaid")}
              </Button>

              <Button
                variant={paymentStatus === "deposit" ? "contained" : "outlined"}
                onClick={() => setPaymentStatus("deposit")}
              >
                {t("booking.deposit")}
              </Button>

              <Button
                variant={paymentStatus === "paid" ? "contained" : "outlined"}
                onClick={() => setPaymentStatus("paid")}
              >
                {t("booking.paid")}
              </Button>
            </ButtonGroup>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="contained" onClick={handleApply}>
          {t("common.apply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatisticsFilterDialog;
